import { Dialog, DialogContent, IconButton, Typography, withStyles, useMediaQuery, useTheme } from '@material-ui/core'
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import CloseIcon from '@material-ui/icons/Close';
import api from '../../api/client';
import React, { useState, useEffect, useRef } from 'react'
import { useSetRecoilState, useRecoilState, useRecoilValue } from 'recoil'
import ContractorInfo from '../../components/contractor/ContractorInfo'
import Contracts from '../../components/contractor/Contracts'
import Deposits from '../../components/contractor/Deposits'
import Documents from '../../components/contractor/Documents';
import Guarantees from '../../components/contractor/Guarantees'
import Requests from '../../components/contractor/Requests';
import Restrictions from '../../components/contractor/Restrictions'
import Transfers from '../../components/contractor/Transfers'
import TreasuryBills from '../../components/contractor/TreasuryBills';
import PrintDocuments from '../../components/contractor/PrintDocuments'
import Layout from '../../components/layout/Layout'
import { loadingState, userInfoState } from '../../recoil/atoms'
import {
  depositsState, documentsState, guaranteesState, requestsState,
  restrictionsState, summaryState, transfersState, treasuryBillsState, documentTransfersState
} from './atoms'
import Payment from '../Payment';
import ContractSummary from '../../components/contractor/ContractSummary';
import Blink from 'react-blink-text'
import IntroduceAssignmentCard from './IntroduceAssignmentCard'
import CartableCard from './CartableCard'

/** وقتی پیمانکار حوالهها را باز کرد، اعلان «حواله جدید» دیدهشده میشود */
const SeenTransferEffect = () => {
  useEffect(() => {
    api.post('users/notifications/').catch(() => {})
  }, [])
  return null
}

const styles = (theme) => ({
  root: { margin: 0, padding: theme.spacing(2) },
  closeButton: {
    position: 'absolute', right: theme.spacing(1),
    top: theme.spacing(1), color: theme.palette.grey[500],
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

/** Helpers */
const extractResults = (res) => {
  if (!res) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.results)) return d.results;
  return [];
};
const byDateDesc = (a, b, key) =>
  (new Date(b?.[key] || 0).getTime()) - (new Date(a?.[key] || 0).getTime());

const ContractorDashboard = ({ onSwitchRole, activeRole: activeRoleProp }) => {
  const userInfo = useRecoilValue(userInfoState)
  const [contracts, setContracts] = useState([])
  const [loading, setLoading] = useRecoilState(loadingState)
  // در موبایل، دیالوگِ گزارش‌ها تمام‌صفحه باز می‌شود (جدول‌های پهن جا ندارند)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // 🔥 با ورود پیمانکار اگر سابسکرایب نیست صفحه پرداخت باز شود
  const [openGuarantees, setOpenGuarantees] = useState(userInfo.is_subscribed ? -1 : 0)
  const [openDeposits, setOpenDeposits] = useState(-1)
  const [openTransfers, setOpenTransfers] = useState(-1)
  const [openRestrictions, setOpenRestrictions] = useState(-1)
  const [openRequests, setOpenRequests] = useState(-1)
  const [openTreasuryBills, setOpenTreasuryBills] = useState(-1)
  const [openDocuments, setOpenDocuments] = useState(-1)
  const [openPrintDocuments, setOpenPrintDocuments] = useState(-1)
  const [openSummary, setOpenSummary] = useState(-1)

  const setGuarantees = useSetRecoilState(guaranteesState)
  const setDeposits = useSetRecoilState(depositsState)
  const setTransfers = useSetRecoilState(transfersState)
  const setRequests = useSetRecoilState(requestsState)
  const setTreasuryBills = useSetRecoilState(treasuryBillsState)
  const setDocuments = useSetRecoilState(documentsState)
  const setDocumentTransfers = useSetRecoilState(documentTransfersState)
  const setSummary = useSetRecoilState(summaryState)
  const [restrictions, setRestrictions] = useRecoilState(restrictionsState)

  /** ───────────── Fetch Contracts ───────────── */
  useEffect(() => {
    let cancelled = false;
    
    const fetchContracts = async () => {
      try {
        console.log('🔍 getContracts called!');
        console.log('👤 UserInfo:', userInfo);
        
        setLoading(true);
        
        // استفاده از axios مستقیم (همون روش قبلی)
        // اگر کاربر دو نقش داره، نقش فعال رو به بکند اطلاع بده
        const activeRole = activeRoleProp || sessionStorage.getItem('activeRole');
        const params = { contractor: 'me', page: 1, page_size: 200 };
        if (activeRole) params.active_role = activeRole;

        const response = await api.get('contracts/', { params });
        
        console.log('📥 Response received:', response.data);
        
        if (!cancelled) {
          const items = extractResults(response);
          console.log('✅ Extracted items:', items.length);
          
          const sorted = [...items].sort((a, b) => byDateDesc(a, b, 'date'));
          console.log('✅ Setting', sorted.length, 'contracts');
          
          setContracts(sorted);
        }
      } catch (error) {
        console.error('❌ Error fetching contracts:', error);
        if (!cancelled) setContracts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchContracts();
    
    return () => { cancelled = true };
  }, [setLoading, userInfo, activeRoleProp]);

  /** ───────────── Fetch Details for Modals ───────────── */
  useEffect(() => {
    let cancelled = false

    const safeSetLoading = (v) => { if (!cancelled) setLoading(v) }

    const getGuarantees = async (id) => {
      safeSetLoading(true)
      try {
        const response = await api.get(`contracts/${id}/guarantees/`)
        const base = extractResults(response)
        const data = (await Promise.all(base.map(async (value) => {
          try {
            const detailResponse = await api.get(`contracts/${id}/guarantees/${value.id}/`)
            return { ...value, detail: detailResponse.data }
          } catch {
            return { ...value, detail: null }
          }
        }))).filter(Boolean)
        setGuarantees([...data].sort((a, b) => byDateDesc(a, b, 'issue_date')))
      } catch (e) {
        console.error('getGuarantees error', e)
        setGuarantees([])
      } finally {
        safeSetLoading(false)
      }
    }

    const getDeposits = async (id) => {
      safeSetLoading(true)
      try {
        const response = await api.get(`contracts/${id}/deposits/`)
        const base = extractResults(response)
        const data = (await Promise.all(base.map(async (value) => {
          try {
            const detailResponse = await api.get(`contracts/${id}/deposits/${value.id}/`)
            return { ...value, detail: detailResponse.data }
          } catch {
            return { ...value, detail: null }
          }
        }))).filter(Boolean)
        setDeposits([...data].sort((a, b) =>
          (Number(b?.year || 0) - Number(a?.year || 0))
        ))
      } catch (e) {
        console.error('getDeposits error', e)
        setDeposits([])
      } finally {
        safeSetLoading(false)
      }
    }

    const getTransfersFn = async (id) => {
      safeSetLoading(true)
      try {
        const response = await api.get(`contracts/${id}/transfers/`)
        const base = extractResults(response)
        if (userInfo.is_subscribed) {
          const data = (await Promise.all(base.map(async (value) => {
            try {
              const [documentsResponse, detailResponse] = await Promise.all([
                api.get(`contracts/${id}/documents/transfer/${value.id}/`),
                api.get(`contracts/${id}/transfers/${value.id}/`),
              ])
              return {
                ...value,
                detail: detailResponse.data,
                documents: extractResults(documentsResponse) || documentsResponse.data
              }
            } catch {
              return { ...value, detail: null, documents: [] }
            }
          }))).filter(Boolean)
          setTransfers([...data].sort((a, b) => byDateDesc(a, b, 'date')))
        } else {
          const data = base.map(v => ({ ...v, detail: [], documents: [] }))
          setTransfers([...data].sort((a, b) => byDateDesc(a, b, 'date')))
        }
      } catch (e) {
        console.error('getTransfers error', e)
        setTransfers([])
      } finally {
        safeSetLoading(false)
      }
    }

    const getRestrictionsFn = async (id) => {
      safeSetLoading(true)
      try {
        const response = id === 'globalRestrictions'
          ? await api.get('contracts/restrictions/')
          : await api.get(`contracts/${id}/restrictions/`)
        const data = extractResults(response)
        setRestrictions([...data].sort((a, b) => byDateDesc(a, b, 'start_date')))
      } catch (e) {
        console.error('getRestrictions error', e)
        setRestrictions([])
      } finally {
        safeSetLoading(false)
      }
    }

    const getRequestsFn = async (id) => {
      safeSetLoading(true)
      try {
        const response = await api.get(`contracts/${id}/requests/`)
        const base = extractResults(response)
        const data = (await Promise.all(base.map(async (value) => {
          try {
            const [detailResponse, documentsResponse] = await Promise.all([
              api.get(`contracts/${id}/requests/${value.id}/transfers/`),
              api.get(`contracts/${id}/documents/request/${value.id}/`)
            ])
            return { 
              ...value, 
              detail: detailResponse.data || [],
              documents: documentsResponse.data || []
            }
          } catch (error) {
            console.error('Error fetching request details:', error)
            return { ...value, detail: [], documents: [] }
          }
        }))).filter(Boolean)
        setRequests([...data].sort((a, b) =>
          (Number(b?.number || 0) - Number(a?.number || 0))
        ))
      } catch (e) {
        console.error('getRequests error', e)
        setRequests([])
      } finally {
        safeSetLoading(false)
      }
    }

    const getTreasuryBillsFn = async (id) => {
      safeSetLoading(true)
      try {
        const response = await api.get(`contracts/${id}/treasury_bills/`)
        const data = extractResults(response)
        setTreasuryBills({
          data: [...data].sort((a, b) => byDateDesc(a, b, 'invoice_date')),
          contract: contracts.find(c => c.id === id)
        })
      } catch (e) {
        console.error('getTreasuryBills error', e)
        setTreasuryBills({ data: [], contract: contracts.find(c => c.id === id) })
      } finally {
        safeSetLoading(false)
      }
    }

    const getDocumentsFn = async (id) => {
      safeSetLoading(true)
      try {
        const response = await api.get(`contracts/${id}/documents/`)
        const data = extractResults(response)
        setDocuments({
          data: [...data].sort((a, b) => byDateDesc(a, b, 'date')),
          contract: contracts.find(c => c.id === id)
        })
      } catch (e) {
        console.error('getDocuments error', e)
        setDocuments({ data: [], contract: contracts.find(c => c.id === id) })
      } finally {
        safeSetLoading(false)
      }
    }

    const getDocumentTransfersFn = async (id) => {
      safeSetLoading(true)
      try {
        const response = await api.get(`contracts/documents/document_transfer/contract/${id}/`)
        const data = extractResults(response)
        setDocumentTransfers([...data].sort((a, b) => byDateDesc(a, b, 'date')))
      } catch (e) {
        console.error('getDocumentTransfers error', e)
        setDocumentTransfers([])
      } finally {
        safeSetLoading(false)
      }
    }

    const getSummaryFn = async (id) => {
      safeSetLoading(true)
      try {
        setSummary(contracts.find(c => c.id === id) || null)
      } finally {
        safeSetLoading(false)
      }
    }

    if (openGuarantees !== -1 && userInfo.is_subscribed) getGuarantees(openGuarantees)
    if (openDeposits !== -1 && userInfo.is_subscribed) getDeposits(openDeposits)
    if (openTransfers !== -1 && userInfo.is_subscribed) getTransfersFn(openTransfers)
    if (openRestrictions !== -1 && userInfo.is_subscribed) getRestrictionsFn(openRestrictions)
    if (openRequests !== -1 && userInfo.is_subscribed) getRequestsFn(openRequests)
    if (openTreasuryBills !== -1 && userInfo.is_subscribed) getTreasuryBillsFn(openTreasuryBills)
    if (openDocuments !== -1 && userInfo.is_subscribed) getDocumentsFn(openDocuments)
    if (openPrintDocuments !== -1) getDocumentTransfersFn(openPrintDocuments)
    if (openSummary !== -1 && userInfo.is_subscribed) getSummaryFn(openSummary)

    return () => { cancelled = true }
  }, [
    contracts,
    openDeposits, openDocuments, openGuarantees, openPrintDocuments,
    openRequests, openRestrictions, openSummary, openTransfers, openTreasuryBills,
    setDeposits, setDocumentTransfers, setDocuments, setGuarantees, setLoading,
    setRequests, setRestrictions, setSummary, setTransfers, setTreasuryBills,
    userInfo.is_subscribed
  ])

  // «تلاش مسدودشده»: کاربرِ بدون اشتراک دیالوگ عضویت را باز کرده است
  const blockPingedRef = useRef(false)
  useEffect(() => {
    const open = (
      openGuarantees !== -1 || openDeposits !== -1 || openTransfers !== -1 ||
      openRestrictions !== -1 || (Array.isArray(restrictions) && restrictions.length > 0) ||
      openTreasuryBills !== -1 || openRequests !== -1 || openDocuments !== -1 ||
      openPrintDocuments !== -1 || openSummary !== -1
    )
    if (open && !userInfo.is_subscribed) {
      if (!blockPingedRef.current) {
        blockPingedRef.current = true
        api.post('users/premium-blocked/').catch(() => {})
      }
    } else if (!open) {
      blockPingedRef.current = false
    }
  }, [
    openGuarantees, openDeposits, openTransfers, openRestrictions, openTreasuryBills,
    openRequests, openDocuments, openPrintDocuments, openSummary, restrictions,
    userInfo.is_subscribed,
  ])

  const closeDialog = () => {
    setOpenGuarantees(-1)
    setOpenDeposits(-1)
    setOpenTransfers(-1)
    setOpenRestrictions(-1)
    setOpenRequests(-1)
    setOpenTreasuryBills(-1)
    setOpenDocuments(-1)
    setOpenSummary(-1)
    setOpenPrintDocuments(-1)
    setRestrictions([])
  }

  return (
    <Layout onSwitchRole={onSwitchRole}>
      <ContractorInfo
        userInfo={userInfo}
        setOpenRestrictions={setOpenRestrictions}
      />

      <IntroduceAssignmentCard />

      <CartableCard />

      <Contracts
        contracts={contracts}
        setOpenGuarantees={setOpenGuarantees}
        setOpenDeposits={setOpenDeposits}
        setOpenTransfers={setOpenTransfers}
        setOpenRestrictions={setOpenRestrictions}
        setOpenRequests={setOpenRequests}
        setOpenTreasuryBills={setOpenTreasuryBills}
        setOpenDocuments={setOpenDocuments}
        setOpenSummary={setOpenSummary}
        setOpenPrintDocuments={setOpenPrintDocuments}
      />

      <Dialog
        style={{ direction: 'rtl' }}
        fullWidth
        fullScreen={isMobile}
        open={
          openGuarantees !== -1 ||
          openDeposits !== -1 ||
          openTransfers !== -1 ||
          openRestrictions !== -1 ||
          (Array.isArray(restrictions) && restrictions.length > 0) ||
          openTreasuryBills !== -1 ||
          openRequests !== -1 ||
          openDocuments !== -1 ||
          openPrintDocuments !== -1 ||
          openSummary !== -1
        }
        onClose={closeDialog}
        maxWidth='xl'
      >
        {userInfo.is_subscribed ? (
          <>
            {openGuarantees !== -1 && !loading && (
              <>
                <DialogTitle onClose={closeDialog}>ضمانت نامه ها</DialogTitle>
                <DialogContent><Guarantees /></DialogContent>
              </>
            )}
            {openDeposits !== -1 && !loading && (
              <>
                <DialogTitle onClose={closeDialog}>سپرده ها</DialogTitle>
                <DialogContent>
                  <Deposits contract={contracts.find(c => c.id === openDeposits)} />
                </DialogContent>
              </>
            )}
            {Array.isArray(restrictions) && restrictions.length > 0 && (
              <>
                <DialogTitle onClose={closeDialog}>محدودیت ها</DialogTitle>
                <DialogContent><Restrictions /></DialogContent>
              </>
            )}
            {openRequests !== -1 && !loading && (
              <>
                <DialogTitle onClose={closeDialog}>صورت وضعیت</DialogTitle>
                <DialogContent>
                  <Requests contract={contracts.find(c => c.id === openRequests)} />
                </DialogContent>
              </>
            )}
            {openTreasuryBills !== -1 && !loading && (
              <>
                <DialogTitle onClose={closeDialog}>اسناد خزانه</DialogTitle>
                <DialogContent><TreasuryBills contract={contracts.find(c => c.id === openTreasuryBills)} /></DialogContent>
              </>
            )}
            {openDocuments !== -1 && !loading && (
              <>
                <DialogTitle onClose={closeDialog}>سند حسابداری</DialogTitle>
                <DialogContent>
                  <Documents contract={contracts.find(c => c.id === openDocuments)} />
                </DialogContent>
              </>
            )}
            {openTransfers !== -1 && !loading && (
              <>
                <DialogTitle onClose={closeDialog}>حواله ها</DialogTitle>
                <DialogContent><Transfers contract={contracts.find(c => c.id === openTransfers)} /></DialogContent>
                <SeenTransferEffect />
              </>
            )}
            {openSummary !== -1 && !loading && (
              <>
                <DialogTitle onClose={closeDialog}>خلاصه قرارداد</DialogTitle>
                <DialogContent><ContractSummary data={contracts} /></DialogContent>
              </>
            )}
            {openPrintDocuments !== -1 && !loading && (
              <>
                <DialogTitle onClose={closeDialog}>اعلامیه ها</DialogTitle>
                <DialogContent>
                  <Typography>
                    <Blink color='red' text='قبل از چاپ اعلامیه، از باز بودن Popup در مرورگر خود اطمینان حاصل فرمایید.' fontSize='100' />
                  </Typography>
                  <PrintDocuments contract={contracts.find(c => c.id === openPrintDocuments)} />
                </DialogContent>
              </>
            )}
          </>
        ) : (
          <>
            {openPrintDocuments !== -1 ? (!loading && (
              <>
                <DialogTitle onClose={closeDialog}>اعلامیه ها</DialogTitle>
                <DialogContent>
                  <Typography>
                    <Blink color='red' text='قبل از چاپ اعلامیه، از باز بودن Popup در مرورگر خود اطمینان حاصل فرمایید.' fontSize='100' />
                  </Typography>
                  <PrintDocuments contract={contracts.find(c => c.id === openPrintDocuments)} />
                </DialogContent>
              </>
            )) : (
              <>
                <DialogTitle onClose={closeDialog}>عضویت</DialogTitle>
                <DialogContent><Payment /></DialogContent>
              </>
            )}
          </>
        )}
      </Dialog>
    </Layout>
  )
}

export default ContractorDashboard